import { useCallback, useRef, useState } from 'react';

const SLOT_ATTR = 'data-panel-slot';

/** Resolve which panel slot is under a screen point (touch / pointer hit-test). */
function resolveSlotAtPoint<T extends string>(clientX: number, clientY: number): T | null {
  const els = document.elementsFromPoint(clientX, clientY);
  for (const el of els) {
    const slot = el.closest(`[${SLOT_ATTR}]`);
    if (!slot) continue;
    const id = slot.getAttribute(SLOT_ATTR);
    if (id) return id as T;
  }
  return null;
}

/**
 * Touch-first vertical panel reorder — pointer drag on handle, hit-test drop target.
 * Replaces HTML5 draggable (does not work on iPhone).
 */
export function usePointerPanelReorder<T extends string>(onReorder: (from: T, to: T) => void) {
  const [dragId, setDragId] = useState<T | null>(null);
  const [overId, setOverId] = useState<T | null>(null);
  const dragIdRef = useRef<T | null>(null);

  const beginReorder = useCallback(
    (id: T, e: React.PointerEvent) => {
      e.preventDefault();
      dragIdRef.current = id;
      setDragId(id);
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const pointerId = e.pointerId;

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const slotId = resolveSlotAtPoint<T>(ev.clientX, ev.clientY);
        setOverId(slotId && slotId !== dragIdRef.current ? slotId : null);
      };

      const end = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          /* */
        }
        const from = dragIdRef.current;
        const to = resolveSlotAtPoint<T>(ev.clientX, ev.clientY);
        if (from && to && from !== to) onReorder(from, to);
        dragIdRef.current = null;
        setDragId(null);
        setOverId(null);
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
    },
    [onReorder],
  );

  return { dragId, overId, beginReorder, slotAttr: SLOT_ATTR };
}
